import type { ProviderResult } from '@/lib/pricing'
import { fmtUSD } from '@/lib/pricing'
import {ArrowDownRight, ArrowUpRight} from 'lucide-react'

interface Props {
  results: ProviderResult[]
}

function CostMatrix({ results }: Props) {
  const cheapest = results[0]
  const priciest = results[results.length - 1]

  return (
    <div className="rounded-2xl border border-[#1F2A44] bg-[#0E1525]/80 backdrop-blur-sm overflow-hidden">
      <div className="px-5 py-3.5 border-b border-[#1F2A44] flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-ring"></div>
          <h3 className="text-sm font-semibold text-slate-200 tracking-tight">Live Cost Matrix</h3>
          <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">monthly · USD</span>
        </div>
        <span className="text-[11px] font-mono text-slate-500">{results.length} providers</span>
      </div>

      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-[#1F2A44]/60">
              <th className="text-left font-medium px-5 py-3">Provider</th>
              <th className="text-right font-medium px-3 py-3">Compute</th>
              <th className="text-right font-medium px-3 py-3">Storage</th>
              <th className="text-right font-medium px-3 py-3">Network</th>
              <th className="text-right font-medium px-3 py-3">Serverless</th>
              <th className="text-right font-medium px-5 py-3">Total / mo</th>
              <th className="text-right font-medium px-5 py-3">vs. min</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {results.map((r) => {
              const isCheapest = r.provider.id === cheapest.provider.id
              const delta = ((r.total - cheapest.total) / cheapest.total) * 100
              return (
                <tr
                  key={r.provider.id}
                  className={`border-b border-[#1F2A44]/40 last:border-0 transition-colors ${
                    isCheapest ? 'bg-emerald-500/[0.04]' : 'hover:bg-white/[0.02]'
                  }`}
                >
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-2 h-8 rounded-full"
                        style={{ background: r.provider.color }}
                      />
                      <div>
                        <div className="font-sans font-semibold text-slate-100 text-[13px]">
                          {r.provider.shortName}
                          {isCheapest && (
                            <span className="ml-2 inline-flex items-center text-[9px] font-mono uppercase tracking-wider text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded px-1.5 py-0.5">
                              Best
                            </span>
                          )}
                        </div>
                        <div className="font-sans text-[11px] text-slate-500 mt-0.5">{r.provider.computeService}</div>
                      </div>
                    </div>
                  </td>
                  <td className="text-right px-3 py-4 text-slate-300 tabular-nums">{fmtUSD(r.compute)}</td>
                  <td className="text-right px-3 py-4 text-slate-300 tabular-nums">{fmtUSD(r.storage)}</td>
                  <td className="text-right px-3 py-4 text-slate-300 tabular-nums">{fmtUSD(r.network)}</td>
                  <td className="text-right px-3 py-4 text-slate-300 tabular-nums">{fmtUSD(r.serverless)}</td>
                  <td className="text-right px-5 py-4">
                    <span className={`text-[15px] font-bold tabular-nums ${isCheapest ? 'text-emerald-300' : 'text-slate-100'}`}>
                      {fmtUSD(r.total)}
                    </span>
                  </td>
                  <td className="text-right px-5 py-4">
                    {isCheapest ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-400">
                        <ArrowDownRight className="w-3 h-3" /> baseline
                      </span>
                    ) : (
                      <span className={`inline-flex items-center gap-1 text-[11px] ${
                        r.provider.id === priciest.provider.id ? 'text-rose-400' : 'text-amber-400/80'
                      }`}>
                        <ArrowUpRight className="w-3 h-3" />+{delta.toFixed(1)}%
                      </span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default CostMatrix
